import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { SearchBar } from '../components/ui/SearchBar';
import { Select } from '../components/ui/Select';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import { Alert } from '../components/ui/Alert';
import { Sheet } from '../components/ui/Sheet';
import { toast } from 'sonner';
import { apiFetch, parseJsonResponse, isNetworkErrorResponse, getImageUrl } from '../utils/api';

interface Banner {
  id: number;
  establishment_id: number | null;
  screen_id: number | null;
  name: string;
  image_path: string;
  image_url?: string | null;
  display_order: number;
  is_active: number;
  is_shared?: number;
  created_at: string;
  updated_at: string;
  banner_type?: 'global' | 'owner' | 'shared';
  associated_establishments?: Array<{ id: number; name_establishment: string }>;
}

interface Establishment {
  id: number;
  name_establishment: string;
  code_establishment_hioffice: number;
  is_active: number;
}

export const Banners: React.FC = () => {
  const { token } = useAuth();
  const [banners, setBanners] = useState<Banner[]>([]);
  const [establishments, setEstablishments] = useState<Establishment[]>([]);
  const [selectedEstablishmentId, setSelectedEstablishmentId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [loadingEstablishments, setLoadingEstablishments] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingBanner, setEditingBanner] = useState<Banner | null>(null);
  const [previewBanner, setPreviewBanner] = useState<Banner | null>(null);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [fileSize, setFileSize] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    image_path: '',
    display_order: 0,
    is_active: 1,
    is_shared: 0,
    establishment_ids: [] as number[]
  });

  // Cargar establecimientos
  useEffect(() => {
    const loadEstablishments = async () => {
      if (!token) {
        setLoadingEstablishments(false);
        return;
      }

      setLoadingEstablishments(true);
      try {
        const params = new URLSearchParams({
          page: '1',
          limit: '1000'
        });

        const route = `establishments/list&${params}`;
        const res = await apiFetch(route, {}, token);
        
        if (isNetworkErrorResponse(res)) {
          setError('Error de conexión al cargar establecimientos');
          return;
        }

        const json = await parseJsonResponse(res);

        if (!res.ok || json.error) {
          const errorMessage = typeof json.error === 'string' ? json.error : json.message || json.data?.error || 'Error al cargar establecimientos';
          throw new Error(errorMessage);
        }

        setEstablishments(json.data?.establishments || []);
      } catch (err: any) {
        console.error('Error cargando establecimientos:', err);
        setError(err.message || 'Error al cargar establecimientos');
      } finally {
        setLoadingEstablishments(false);
      }
    };

    loadEstablishments();
  }, [token]);

  // Cargar banners cuando se selecciona un establecimiento o al inicio (banners globales)
  useEffect(() => {
    if (token) {
      loadBanners();
    } else {
      setBanners([]);
    }
  }, [selectedEstablishmentId, token]);

  // Función helper para formatear el tamaño del archivo
  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  const loadBanners = async () => {
    if (!token) return;
    
    setLoading(true);
    try {
      const API_BASE_URL = process.env.NODE_ENV === 'production' ? '/api' : 'http://hiomenu.local/api';
      // Si hay establecimiento seleccionado, incluirlo; si no, cargar solo globales
      const url = selectedEstablishmentId 
        ? `${API_BASE_URL}.php?route=banners/list&establishment_id=${selectedEstablishmentId}`
        : `${API_BASE_URL}.php?route=banners/list`;
      
      console.log('📋 [Banners] Cargando banners...');
      console.log('📋 [Banners] Establecimiento seleccionado:', selectedEstablishmentId || 'NINGUNO (solo globales)');
      console.log('📋 [Banners] URL:', url);
      
      const res = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });
      
      console.log('📥 [Banners] Respuesta HTTP:', {
        status: res.status,
        statusText: res.statusText,
        ok: res.ok
      });
      
      if (isNetworkErrorResponse(res)) {
        console.error('❌ [Banners] Error de red al cargar banners');
        toast.error('Error de conexión al cargar banners');
        return;
      }
      
      const json = await parseJsonResponse(res);
      console.log('✅ [Banners] Respuesta parseada completa:', json);
      
      // Verificar errores primero
      if (!res.ok || json.error) {
        const errorMessage = typeof json.error === 'string' ? json.error : json.message || 'Error al cargar banners';
        console.error('❌ [Banners] Error en respuesta:', errorMessage);
        toast.error(errorMessage);
        setBanners([]);
        return;
      }
      
      // Extraer banners de la respuesta
      // El backend retorna: { success: true, data: { banners: [...], total: ... } }
      let banners: Banner[] = [];
      if (json.data?.banners) {
        banners = Array.isArray(json.data.banners) ? json.data.banners : [];
      } else if (json.data && Array.isArray(json.data)) {
        // Fallback: si data es directamente un array
        banners = json.data;
      }
      
      console.log('📋 [Banners] Banners extraídos:', banners.length);
      banners.forEach((banner: Banner, index: number) => {
        console.log(`📋 [Banners] Banner ${index + 1}:`, {
          id: banner.id,
          name: banner.name,
          image_path: banner.image_path,
          image_url: banner.image_url,
          display_order: banner.display_order,
          is_active: banner.is_active,
          is_shared: banner.is_shared
        });
      });
      
      // Aplicar búsqueda si existe
      let filteredBanners = banners;
      if (search) {
        const searchLower = search.toLowerCase();
        filteredBanners = banners.filter((b: Banner) =>
          b.name.toLowerCase().includes(searchLower)
        );
        console.log('🔍 [Banners] Banners después de filtrar:', filteredBanners.length);
      }
      
      setBanners(filteredBanners);
      console.log('✅ [Banners] Banners cargados en estado:', filteredBanners.length);
      
      if (filteredBanners.length === 0 && banners.length === 0) {
        console.warn('⚠️ [Banners] No se encontraron banners para este establecimiento');
      }
    } catch (error) {
      console.error('❌ [Banners] Error cargando banners:', error);
      toast.error('Error al cargar banners');
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const maxSize = 5 * 1024 * 1024; // 5MB
    
    // Guardar el tamaño del archivo
    setFileSize(file.size);
    
    // Mostrar warning si es más grande, pero permitir continuar
    if (file.size > maxSize) {
      toast.warning(`El archivo es de ${formatFileSize(file.size)}, excede el límite de 5MB. La subida podría fallar.`);
    }

    const extension = file.name.split('.').pop()?.toLowerCase();
    const allowedTypes = ['png', 'jpg', 'jpeg', 'gif'];
    if (!extension || !allowedTypes.includes(extension)) {
      toast.error('Tipo de archivo no permitido. Formatos permitidos: PNG, JPG, GIF');
      setFileSize(null);
      return;
    }

    setImageFile(file);
    
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result as string);
    };
    reader.readAsDataURL(file);
  };

  const uploadImage = async (): Promise<{ imagePath: string | null; imageUrl: string | null } | null> => {
    if (!imageFile || !token) return null;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('banner_image', imageFile);
      
      // Debug: Verificar que el archivo esté en el FormData
      console.log('📤 [Banners] Archivo a subir:', {
        name: imageFile.name,
        size: imageFile.size,
        type: imageFile.type
      });

      const API_BASE_URL = process.env.NODE_ENV === 'production' ? '/api' : 'http://hiomenu.local/api';
      const url = `${API_BASE_URL}.php?route=banners/upload-image`;
      console.log('📡 [Banners] URL de subida:', url);
      
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
          // NO agregar Content-Type aquí - el navegador lo establece automáticamente con el boundary
        },
        body: formData
      });

      console.log('📥 [Banners] Respuesta HTTP:', {
        status: res.status,
        statusText: res.statusText,
        ok: res.ok,
        contentType: res.headers.get('content-type')
      });

      if (!res.ok) {
        const text = await res.text();
        console.error('❌ [Banners] Respuesta de error (texto):', text.substring(0, 500));
        let json;
        try {
          json = JSON.parse(text);
        } catch (e) {
          console.error('❌ [Banners] No se pudo parsear JSON:', e);
          throw new Error(`Error del servidor (${res.status}): ${text.substring(0, 200)}`);
        }
        console.error('❌ [Banners] Error del servidor (JSON):', json);
        const errorMessage = typeof json.error === 'string' ? json.error : json.message || 'Error al subir imagen';
        throw new Error(errorMessage);
      }

      const text = await res.text();
      console.log('✅ [Banners] Respuesta exitosa (texto):', text.substring(0, 500));
      
      let json;
      try {
        json = JSON.parse(text);
      } catch (e) {
        console.error('❌ [Banners] Error parseando JSON de respuesta:', e);
        console.error('❌ [Banners] Texto completo:', text);
        throw new Error('Respuesta inválida del servidor');
      }
      
      console.log('✅ [Banners] Respuesta exitosa (JSON):', json);
      
      const responseData = json.data || json;
      const imagePath = responseData.image_path || null;
      const imageUrl = responseData.image_url || null;
      
      console.log('🖼️ [Banners] Ruta de imagen obtenida:', imagePath);
      console.log('🖼️ [Banners] URL de imagen obtenida:', imageUrl);
      
      if (!imagePath) {
        console.error('⚠️ [Banners] ADVERTENCIA: No se recibió image_path en la respuesta');
        console.error('⚠️ [Banners] Respuesta completa:', JSON.stringify(json, null, 2));
      }
      
      // Retornar objeto con ambos valores si están disponibles
      return { imagePath, imageUrl };
    } catch (err: any) {
      console.error('❌ [Banners] Error completo en uploadImage:', err);
      console.error('❌ [Banners] Stack trace:', err.stack);
      toast.error(err.message || 'Error al subir imagen');
      throw err;
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e?: React.FormEvent | React.MouseEvent) => {
    if (e) {
      e.preventDefault();
    }
    if (!token) return;
    
    // Validar que si no es compartido globalmente, tenga establecimiento seleccionado
    if (formData.is_shared === 0 && !selectedEstablishmentId) {
      toast.error('Debes seleccionar un establecimiento para banners no compartidos');
      return;
    }

    try {
      let imagePath = formData.image_path;
      console.log('💾 [Banners] Iniciando guardado de banner...');
      console.log('💾 [Banners] imagePath inicial:', imagePath);
      console.log('💾 [Banners] imageFile:', imageFile ? { name: imageFile.name, size: imageFile.size } : null);
      console.log('💾 [Banners] editingBanner:', editingBanner ? { id: editingBanner.id, name: editingBanner.name } : null);

      if (imageFile) {
        console.log('📤 [Banners] Subiendo imagen...');
        const uploadResult = await uploadImage();
        console.log('📤 [Banners] Resultado de subida:', uploadResult);
        if (!uploadResult || !uploadResult.imagePath) {
          toast.error('Error al subir la imagen');
          return;
        }
        imagePath = uploadResult.imagePath;
        console.log('💾 [Banners] imagePath actualizado:', imagePath);
        // Si tenemos image_url, actualizar el preview también
        if (uploadResult.imageUrl) {
          setImagePreview(uploadResult.imageUrl);
        }
      }

      if (!imagePath) {
        console.error('❌ [Banners] No hay imagePath disponible');
        toast.error('Debes subir una imagen');
        return;
      }

      const payload: any = {
        ...formData,
        establishment_id: formData.is_shared ? null : parseInt(selectedEstablishmentId, 10),
        image_path: imagePath,
        display_order: parseInt(String(formData.display_order), 10) || 0,
        is_active: formData.is_active ? 1 : 0,
        is_shared: formData.is_shared ? 1 : 0,
        establishment_ids: formData.is_shared ? [] : formData.establishment_ids
      };

      const route = editingBanner ? 'banners/update' : 'banners/create';
      
      if (editingBanner) {
        payload.id = editingBanner.id;
      }

      console.log('📦 [Banners] Payload a enviar:', payload);
      console.log('📡 [Banners] Ruta de API:', route);

      const res = await apiFetch(
        route,
        {
          method: 'POST',
          body: JSON.stringify(payload)
        },
        token
      );

      console.log('📥 [Banners] Respuesta de apiFetch:', res);

      if (isNetworkErrorResponse(res)) {
        console.error('❌ [Banners] Error de red');
        toast.error('Error de conexión');
        return;
      }

      const json = await parseJsonResponse(res);
      console.log('✅ [Banners] Respuesta parseada:', json);

      if (json.error || !json.success) {
        let errorMessage = editingBanner ? 'Error al actualizar banner' : 'Error al crear banner';
        if (json.error) {
          errorMessage = typeof json.error === 'string' ? json.error : String(json.error);
        } else if (json.message) {
          errorMessage = typeof json.message === 'string' ? json.message : String(json.message);
        }
        console.error('❌ [Banners] Error en respuesta:', errorMessage);
        toast.error(errorMessage);
      } else {
        console.log('✅ [Banners] Banner guardado exitosamente');
        toast.success(editingBanner ? 'Banner actualizado exitosamente' : 'Banner creado exitosamente');
        resetForm();
        loadBanners();
      }
    } catch (error: any) {
      console.error('❌ [Banners] Error al guardar banner:', error);
      console.error('❌ [Banners] Stack trace:', error.stack);
      toast.error(error.message || 'Error al guardar banner');
    }
  };

  const handleEdit = async (banner: Banner) => {
    setEditingBanner(banner);
    setFormData({
      name: banner.name,
      image_path: banner.image_path,
      display_order: banner.display_order,
      is_active: banner.is_active,
      is_shared: banner.is_shared || 0,
      establishment_ids: banner.associated_establishments?.map(e => e.id) || []
    });
    setImagePreview(banner.image_url || getImageUrl(banner.image_path) || null);
    setImageFile(null);
    setFileSize(null);
    setShowForm(true);
  };

  const handleDelete = async (bannerId: number) => {
    if (!token) return;
    if (!window.confirm('¿Estás seguro de que deseas eliminar este banner?')) return;

    try {
      const res = await apiFetch(
        'banners/delete',
        {
          method: 'DELETE',
          body: JSON.stringify({ id: bannerId })
        },
        token
      );

      if (isNetworkErrorResponse(res)) {
        toast.error('Error de conexión');
        return;
      }

      const json = await parseJsonResponse(res);

      if (json.error || !json.success) {
        let errorMessage = 'Error al eliminar banner';
        if (json.error) {
          errorMessage = typeof json.error === 'string' ? json.error : String(json.error);
        } else if (json.message) {
          errorMessage = typeof json.message === 'string' ? json.message : String(json.message);
        }
        toast.error(errorMessage);
      } else {
        toast.success('Banner eliminado exitosamente');
        loadBanners();
      }
    } catch (error: any) {
      console.error('Error al eliminar banner:', error);
      toast.error('Error al eliminar banner');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      image_path: '',
      display_order: 0,
      is_active: 1,
      is_shared: selectedEstablishmentId ? 0 : 1, // Si no hay establecimiento, default a global
      establishment_ids: []
    });
    setImageFile(null);
    setImagePreview(null);
    setFileSize(null);
    setEditingBanner(null);
    setShowForm(false);
  };

  const handleCreate = () => {
    resetForm();
    
    // Si no hay establecimiento seleccionado, forzar banner global
    if (!selectedEstablishmentId) {
      setFormData(prev => ({
        ...prev,
        is_shared: 1  // Forzar a global si no hay establecimiento
      }));
    }
    
    setShowForm(true);
  };

  const handleFormCancel = () => {
    resetForm();
  };

  const handleSearch = () => {
    if (selectedEstablishmentId) {
      loadBanners();
    }
  };

  // Filtrar banners por búsqueda
  const filteredBanners = search
    ? banners.filter(b =>
        b.name.toLowerCase().includes(search.toLowerCase())
      )
    : banners;

  return (
    <DashboardLayout
      title="Gestión de Banners"
      subtitle="Administra los banners de los establecimientos"
      showBackButton
      backPath="/management"
    >
      {/* Sheet para formulario */}
      <Sheet
        isOpen={showForm}
        onClose={handleFormCancel}
        title={editingBanner ? 'Editar Banner' : 'Nuevo Banner'}
        subtitle={editingBanner ? 'Modifica la información del banner' : 'Completa los datos para crear un nuevo banner'}
        size="lg"
      >
        <div className="space-y-4">
          <Input
            label="Nombre del Banner"
            name="name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            placeholder="Ej: Banner de Promoción"
            required
          />

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Imagen del Banner *
            </label>
            <input
              type="file"
              accept=".png,.jpg,.jpeg,.gif"
              onChange={handleImageChange}
              className="block w-full text-sm text-gray-500
                file:mr-4 file:py-2 file:px-4
                file:rounded-lg file:border-0
                file:text-sm file:font-semibold
                file:bg-red-50 file:text-red-700
                hover:file:bg-red-100
                file:cursor-pointer"
            />
            <p className="mt-1 text-xs text-gray-500">
              Formatos permitidos: PNG, JPG, GIF. Tamaño máximo: 5MB
            </p>
            {imagePreview && (
              <div className="mt-4">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="max-w-full h-auto max-h-48 rounded-lg border border-gray-300"
                />
                {fileSize !== null && (
                  <div className="mt-2 flex items-center justify-between text-xs">
                    <span className={`font-medium ${
                      fileSize > 5 * 1024 * 1024 
                        ? 'text-orange-600' 
                        : 'text-gray-600'
                    }`}>
                      Tamaño: {formatFileSize(fileSize)}
                    </span>
                    {fileSize > 5 * 1024 * 1024 && (
                      <span className="text-orange-600 font-semibold">
                        ⚠️ Excede el límite
                      </span>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Orden de Visualización"
              type="number"
              name="display_order"
              value={formData.display_order}
              onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value, 10) || 0 })}
              min="0"
            />

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Estado
              </label>
              <select
                value={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: parseInt(e.target.value, 10) })}
                className="w-full px-4 py-3 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                <option value={1}>Activo</option>
                <option value={0}>Inactivo</option>
              </select>
            </div>
          </div>

          {/* Opciones de Compartir */}
          <div className="border-t pt-4 space-y-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="is_shared"
                checked={formData.is_shared === 1}
                disabled={!selectedEstablishmentId} // Deshabilitar si no hay establecimiento
                onChange={(e) => {
                  if (!selectedEstablishmentId) {
                    toast.info('Para crear un banner específico, primero selecciona un establecimiento');
                    return;
                  }
                  const isShared = e.target.checked ? 1 : 0;
                  setFormData({
                    ...formData,
                    is_shared: isShared,
                    establishment_ids: isShared ? [] : formData.establishment_ids
                  });
                }}
                className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded disabled:opacity-50 disabled:cursor-not-allowed"
              />
              <label htmlFor="is_shared" className={`ml-2 block text-sm font-semibold ${!selectedEstablishmentId ? 'text-gray-500' : 'text-gray-700'}`}>
                Compartir con todos los establecimientos
              </label>
            </div>
            {!selectedEstablishmentId && (
              <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Creando banner global. Para crear un banner específico, selecciona un establecimiento primero.
              </p>
            )}
            {formData.is_shared === 0 && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Establecimientos específicos (Opcional)
                </label>
                <select
                  multiple
                  value={formData.establishment_ids.map(id => id.toString())}
                  onChange={(e) => {
                    const selectedIds = Array.from(e.target.selectedOptions, option => parseInt(option.value, 10));
                    setFormData({ ...formData, establishment_ids: selectedIds });
                  }}
                  className="w-full px-4 py-3 text-sm bg-white border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 min-h-[120px]"
                  size={5}
                >
                  {establishments
                    .filter(e => e.is_active === 1 && e.id.toString() !== selectedEstablishmentId)
                    .map(e => (
                      <option key={e.id} value={e.id.toString()}>
                        {e.name_establishment} (Cód: {e.code_establishment_hioffice})
                      </option>
                    ))}
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  Mantén presionada la tecla Ctrl (Cmd en Mac) para seleccionar múltiples establecimientos.
                  Si no seleccionas ninguno, el banner solo será visible en el establecimiento actual.
                </p>
                {formData.establishment_ids.length > 0 && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {formData.establishment_ids.map(id => {
                      const est = establishments.find(e => e.id === id);
                      return est ? (
                        <Badge key={id} variant="neutral" className="text-xs">
                          {est.name_establishment}
                        </Badge>
                      ) : null;
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="flex gap-2 pt-4">
            <Button
              type="button"
              variant="primary"
              isLoading={uploading}
              disabled={uploading}
              onClick={(e) => handleSubmit(e)}
              className="flex-1"
            >
              {editingBanner ? 'Actualizar Banner' : 'Crear Banner'}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={handleFormCancel}
              className="flex-1"
            >
              Cancelar
            </Button>
          </div>
        </div>
      </Sheet>

      <div className="space-y-6">
        {/* Selector de Establecimiento */}
        <Card>
          <CardContent className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-center">
              <div className="flex-1">
                <Select
                  label="Seleccionar Establecimiento (Opcional)"
                  value={selectedEstablishmentId}
                  onChange={(e) => setSelectedEstablishmentId(e.target.value)}
                  options={[
                    { value: '', label: '-- Todos los banners (solo globales) --' },
                    ...establishments.filter(e => e.is_active === 1).map(e => ({
                      value: e.id.toString(),
                      label: `${e.name_establishment} (Cód: ${e.code_establishment_hioffice})`
                    }))
                  ]}
                  disabled={loadingEstablishments}
                />
                <p className="mt-1 text-xs text-gray-500">
                  {selectedEstablishmentId 
                    ? 'Mostrando banners del establecimiento seleccionado y banners globales'
                    : 'Mostrando solo banners compartidos globalmente. Selecciona un establecimiento para ver sus banners específicos.'}
                </p>
              </div>
              <Button
                variant="primary"
                size="md"
                onClick={handleCreate}
                className="whitespace-nowrap"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Agregar Banner
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Error Message */}
        {error && (
          <Alert variant="error">{error}</Alert>
        )}

        {/* Barra de Búsqueda */}
        {banners.length > 0 && (
          <Card>
            <CardContent className="p-4 sm:p-6">
              <SearchBar
                value={search}
                onChange={setSearch}
                onSubmit={handleSearch}
                placeholder="Buscar por nombre de banner..."
                className="w-full"
              />
            </CardContent>
          </Card>
        )}

        {/* Lista de Banners */}
        {loading ? (
          <Card>
            <CardContent>
              <div className="p-12 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-red-600 mb-4"></div>
                <p className="text-gray-600">Cargando banners...</p>
              </div>
            </CardContent>
          </Card>
        ) : filteredBanners.length === 0 ? (
          <Card>
            <CardContent>
              <div className="p-12 text-center text-gray-500">
                <svg className="w-12 h-12 mx-auto mb-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
                <p className="font-medium mb-2">
                  {selectedEstablishmentId 
                    ? 'No hay banners configurados para este establecimiento'
                    : 'No hay banners compartidos globalmente'}
                </p>
                <p className="text-sm">
                  {search
                    ? 'No se encontraron banners con los criterios de búsqueda.'
                    : selectedEstablishmentId
                      ? 'Haz clic en "Agregar Banner" para crear uno nuevo para este establecimiento'
                      : 'Haz clic en "Agregar Banner" y marca "Compartir con todos los establecimientos" para crear un banner global'}
                </p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Separar banners globales y específicos */}
            {(() => {
              const globalBanners = filteredBanners.filter(b => b.is_shared === 1);
              const specificBanners = filteredBanners.filter(b => b.is_shared === 0);
              
              return (
                <>
                  {/* Banners Globales */}
                  {globalBanners.length > 0 && (
                    <div className="mb-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                        <Badge variant="info" className="bg-blue-500 text-white border-blue-600">
                          Global
                        </Badge>
                        Banners Compartidos Globalmente ({globalBanners.length})
                      </h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {globalBanners.map((banner) => (
                              <Card key={banner.id}>
                                <CardContent className="p-4">
                                  <div className="flex items-start justify-between mb-3">
                                    <div className="flex-1 min-w-0">
                                      <h4 className="font-semibold text-gray-900 mb-1 truncate">
                                        {banner.name}
                                      </h4>
                                      <p className="text-xs text-gray-500">
                                        Orden: {banner.display_order}
                                      </p>
                                    </div>
                                    <div className="flex gap-1 flex-wrap ml-2 flex-shrink-0">
                                      {banner.is_active === 1 ? (
                                        <Badge variant="success">
                                          Activo
                                        </Badge>
                                      ) : (
                                        <Badge variant="neutral">
                                          Inactivo
                                        </Badge>
                                      )}
                                      {banner.is_shared === 1 && (
                                        <Badge variant="info" className="bg-blue-500 text-white border-blue-600">
                                          Global
                                        </Badge>
                                      )}
                                    </div>
                                  </div>

                                  {(banner.image_path || banner.image_url) && (
                                    <div className="mb-3">
                                      <img
                                        src={banner.image_url || getImageUrl(banner.image_path) || ''}
                                        alt={banner.name}
                                        className="w-full h-32 object-cover rounded-lg border border-gray-300"
                                        onError={(e) => {
                                          console.error('Error cargando imagen del banner:', banner.name, 'URL:', banner.image_url || getImageUrl(banner.image_path));
                                          (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect width="100" height="100" fill="%23ddd"/%3E%3Ctext x="50" y="50" text-anchor="middle" dy=".3em" fill="%23999"%3EImagen no disponible%3C/text%3E%3C/svg%3E';
                                        }}
                                      />
                                    </div>
                                  )}

                                  <div className="flex gap-2">
                                    <Button
                                      variant="secondary"
                                      size="sm"
                                      onClick={() => setPreviewBanner(banner)}
                                      className="flex items-center justify-center"
                                      title="Previsualizar imagen"
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                      </svg>
                                    </Button>
                                    <Button
                                      variant="secondary"
                                      size="sm"
                                      onClick={() => handleEdit(banner)}
                                      className="flex-1"
                                    >
                                      Editar
                                    </Button>
                                    <Button
                                      variant="secondary"
                                      size="sm"
                                      onClick={() => handleDelete(banner.id)}
                                      className="flex-1"
                                    >
                                      Eliminar
                                    </Button>
                                  </div>
                                </CardContent>
                              </Card>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      {/* Banners Específicos */}
                      {specificBanners.length > 0 && (
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                            <Badge variant="neutral">
                              Específicos
                            </Badge>
                            Banners del Establecimiento ({specificBanners.length})
                          </h3>
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {specificBanners.map((banner) => (
                              <Card key={banner.id}>
                                <CardContent className="p-4">
                                  <div className="flex items-start justify-between mb-3">
                                    <div className="flex-1 min-w-0">
                                      <h4 className="font-semibold text-gray-900 mb-1 truncate">
                                        {banner.name}
                                      </h4>
                                      <p className="text-xs text-gray-500">
                                        Orden: {banner.display_order}
                                      </p>
                                    </div>
                                    <div className="flex gap-1 flex-wrap ml-2 flex-shrink-0">
                                      {banner.is_active === 1 ? (
                                        <Badge variant="success">
                                          Activo
                                        </Badge>
                                      ) : (
                                        <Badge variant="neutral">
                                          Inactivo
                                        </Badge>
                                      )}
                                      {banner.banner_type === 'shared' && banner.is_shared === 0 && (
                                        <Badge variant="neutral" className="bg-purple-500 text-white border-purple-600">
                                          Compartido
                                        </Badge>
                                      )}
                                    </div>
                                  </div>

                                  {(banner.image_path || banner.image_url) && (
                                    <div className="mb-3">
                                      <img
                                        src={banner.image_url || getImageUrl(banner.image_path) || ''}
                                        alt={banner.name}
                                        className="w-full h-32 object-cover rounded-lg border border-gray-300"
                                        onError={(e) => {
                                          console.error('Error cargando imagen del banner:', banner.name, 'URL:', banner.image_url || getImageUrl(banner.image_path));
                                          (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect width="100" height="100" fill="%23ddd"/%3E%3Ctext x="50" y="50" text-anchor="middle" dy=".3em" fill="%23999"%3EImagen no disponible%3C/text%3E%3C/svg%3E';
                                        }}
                                      />
                                    </div>
                                  )}

                                  {banner.associated_establishments && banner.associated_establishments.length > 0 && (
                                    <div className="mb-3">
                                      <p className="text-xs text-gray-500 mb-1 font-semibold">Establecimientos asociados:</p>
                                      <div className="flex flex-wrap gap-1">
                                        {banner.associated_establishments.map(est => (
                                          <Badge key={est.id} variant="neutral" className="text-xs">
                                            {est.name_establishment}
                                          </Badge>
                                        ))}
                                      </div>
                                    </div>
                                  )}

                                  <div className="flex gap-2">
                                    <Button
                                      variant="secondary"
                                      size="sm"
                                      onClick={() => setPreviewBanner(banner)}
                                      className="flex items-center justify-center"
                                      title="Previsualizar imagen"
                                    >
                                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                      </svg>
                                    </Button>
                                    <Button
                                      variant="secondary"
                                      size="sm"
                                      onClick={() => handleEdit(banner)}
                                      className="flex-1"
                                    >
                                      Editar
                                    </Button>
                                    <Button
                                      variant="secondary"
                                      size="sm"
                                      onClick={() => handleDelete(banner.id)}
                                      className="flex-1"
                                    >
                                      Eliminar
                                    </Button>
                                  </div>
                                </CardContent>
                              </Card>
                            ))}
                          </div>
                        </div>
                      )}
                    </>
                  );
                })()}
              </>
            )}

      {/* Modal de Previsualización */}
      {previewBanner && (previewBanner.image_path || previewBanner.image_url) && (
        <Sheet
          isOpen={!!previewBanner}
          onClose={() => setPreviewBanner(null)}
          title={`Previsualización: ${previewBanner.name}`}
          size="xl"
        >
          <div className="flex flex-col items-center justify-center p-4">
            <img
              src={previewBanner.image_url || getImageUrl(previewBanner.image_path) || ''}
              alt={previewBanner.name}
              className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-lg"
              onError={(e) => {
                console.error('Error cargando imagen del banner:', previewBanner.name);
                (e.target as HTMLImageElement).src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect width="100" height="100" fill="%23ddd"/%3E%3Ctext x="50" y="50" text-anchor="middle" dy=".3em" fill="%23999"%3EImagen no disponible%3C/text%3E%3C/svg%3E';
              }}
            />
            <div className="mt-4 text-sm text-gray-600 space-y-1">
              <p><strong>Orden de visualización:</strong> {previewBanner.display_order}</p>
              <p><strong>Estado:</strong> {previewBanner.is_active === 1 ? 'Activo' : 'Inactivo'}</p>
              {previewBanner.associated_establishments && previewBanner.associated_establishments.length > 0 && (
                <div>
                  <p className="font-semibold mb-1">Establecimientos asociados:</p>
                  <div className="flex flex-wrap gap-1">
                    {previewBanner.associated_establishments.map(est => (
                      <Badge key={est.id} variant="neutral" className="text-xs">
                        {est.name_establishment}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </Sheet>
      )}
      </div>
    </DashboardLayout>
  );
};

